import { Match } from '@apis/index'
import { eye, outlineuser } from '@assets/icons'
import Column from '@components/column'
import Status from '@components/status'
import Table from '@components/table'
import { useModal } from '@context/ModalContext'
import MatchroomDetails from '@pages/admin/game-management/match-rooms/detail-modal'
import { RootState } from '@store/index'
import theme from '@utils/theme'
import styled from '@emotion/styled'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Button from '@components/button'
import ResultModal from './result-modal'
import dayjs from 'dayjs'
import IndividualUser from '@pages/admin/user-management/individual-user'

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'Not Started', label: 'Not Started' },
  { value: 'On Going', label: 'On Going' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Review', label: 'Review' },
]

const PlayerCreatedMatches = ({
  isResult,
  userId,
  matchStatus = '',
}: {
  userId?: string
  isResult?: boolean
  matchStatus?: string
}) => {
  const { showModal } = useModal()
  const {
    items: matches = [],
    totalItems = 0,
    totalPages = 1,
    loading,
  } = useSelector((state: RootState) => state.matches)

  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [expandedData, setExpandedData] = useState<Record<string, any[]>>({})
  const [editableInputs, setEditableInputs] = useState<Record<string, any>>({})
  const [matchId, setMatchId] = useState<string | null>(null)
  const [expandedLoading, setExpandedLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState(matchStatus || '')
  const [limit, setLimit] = useState(null)

  const fetchJoinedByUsers = async (matchId: string) => {
    setExpandedLoading(true)
    if (!matchId || expandedData[matchId]) return
    try {
      const result = await Match.joinedBy({
        params: { matchId, limit },
      })

      setExpandedData((prev) => ({
        ...prev,
        [matchId]: result.items || [],
      }))
    } catch (err) {
      console.error(err)
      toast.error('Failed to load players for match')
    } finally {
      setExpandedLoading(false)
    }
  }

  const fetchMatches = async (search = '', page = 1) => {
    try {
      const params: any = {
        limit: 30,
        isCreatedByAdmin: false,
        page,
        ...(userId && { userId }),
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search }),
      }
      await Match.list({
        state: 'matches',
        params,
      })
    } catch (error) {
      console.error('Failed to fetch matches:', error)
    }
  }
  useEffect(() => {
    setPage(1)
  }, [searchTerm])
  useEffect(() => {
    fetchMatches(searchTerm, page)
  }, [page, userId, statusFilter, searchTerm])

  const columns = [
    { title: 'Match ID', key: 'matchId' },
    {
      title: 'Created By',
      key: 'created_By',
      render: (item) => <div>{item.created_By || 'N/A'}</div>,
    },
    { title: 'Matchroom Name', key: 'matchroomName' },
    { title: 'Game Name', key: 'gameName' },
    { title: 'No. of Players', key: 'numberOfPlayers' },
    { title: 'Match Type', key: 'matchType' },
    { title: 'Entry Fee', key: 'entryFee' },
    {
      title: 'Game Time',
      key: 'gameTime',
      render: (item) => (
        <Column stylecolumn="gap:3px">
          <p>{item.gameDate || 'N/A'}</p>
          <p style={{ color: 'gray', fontSize: '12px' }}>
            {dayjs(`1970-01-01T${item.gameTime}`).format('hh:mm A')}
          </p>
        </Column>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (item) => (
        <Status
          item={item}
          statuses={['Completed', 'Not Started', 'Cancelled', 'On Going']}
          onStatusChange={async (status) => {
            if (status === item.status) return
            try {
              await Match.updateStatus({
                id: item.id,
                state: 'matches',
                data: { status },
              })
            } catch (e) {
              toast.error('Failed to update status')
            }
          }}
        />
      ),
    },
  ]

  const data = matches.map((match) => ({
    id: match?.id ?? 'N/A',
    matchId: match?.id ?? 'N/A',
    matchroomName: match?.name ?? 'Unnamed',
    gameName: match?.type ?? 'Unknown',
    created_By: match?.user?.name ?? 'Unknown',
    numberOfPlayers: match?.teamSize ?? 0,
    matchType: match?.infos?.gameSettings?.matchType ?? 'N/A',
    gameTime: match?.slot ?? 'N/A',
    entryFee: match?.entryFee ?? 0,
    gameDate: match?.date ?? 'N/A',
    status: match?.status ?? 'Unknown',
    potentialWinning:
      match?.potentialWinning ?? match?.infos?.potentialWinning ?? 0,
  }))

  const buildChildColumns = (entryFee: number, players: any[]) => {
    const base = [
      {
        title: 'Player Name',
        key: 'player_name',
        render: (item: any) => {
          return (
            <p
              style={{ cursor: 'pointer' }}
              onClick={() => {
                showModal(<IndividualUser userStateId={item.id} />, {
                  width: 1200,
                })
              }}
            >
              {item?.player_name}
            </p>
          )
        },
      },
      { title: 'Phone No.', key: 'phone_no' },
      {
        title: 'PUBG Tag',
        key: 'pubg_tag',
        render: (item) => (
          <span>
            {item.gamer_tag?.pubgGamerTag ? (
              item.gamer_tag.pubgGamerTag
            ) : (
              <span style={{ color: '#999', fontStyle: 'italic' }}>
                No PUBG Tag
              </span>
            )}
          </span>
        ),
      },
      {
        title: 'Free Fire Tag',
        key: 'freefire_tag',
        render: (item) => (
          <span>
            {item.gamer_tag?.freeFireGamerTag ? (
              item.gamer_tag.freeFireGamerTag
            ) : (
              <span style={{ color: '#999', fontStyle: 'italic' }}>
                No Free Fire Tag
              </span>
            )}
          </span>
        ),
      },
    ]

    const editable = [
      {
        title: 'Points',
        key: 'points',
        render: (item: any) => {
          const status =
            editableInputs[item.id]?.claimedStatus || item.claimedStatus
          const isDisabled = ['Win', 'Lose', 'Refund'].includes(status)

          return (
            <Input
              type="text"
              value={editableInputs[item.id]?.points ?? item.points ?? ''}
              disabled={isDisabled}
              onChange={(e) => {
                let val = e.target.value
                setEditableInputs((prev) => ({
                  ...prev,
                  [item.id]: {
                    ...prev[item.id],
                    matchJoinId: item.matchJoinId,
                    userId: item.id,
                    points: val,
                  },
                }))
              }}
            />
          )
        },
      },
      {
        title: 'Claimed Status',
        key: 'claimedStatus',
        render: (item: any) => {
          const currentValue = ['Win', 'Lose', 'Refund'].includes(item?.status)
            ? item.status
            : item?.claimedStatus || ''

          return (
            <Status
              item={{ status: currentValue || 'Select' }}
              statuses={['Win', 'Lose', 'Refund']}
              onStatusChange={(status) => {
                const updates: any = {
                  [item.id]: {
                    ...editableInputs[item.id],
                    matchJoinId: item.matchJoinId,
                    userId: item.id,
                    claimedStatus: status,
                    points:
                      status === 'Win'
                        ? item.potentialWinning
                        : status === 'Lose'
                          ? 0
                          : status === 'Refund'
                            ? entryFee
                            : editableInputs[item.id]?.points || 0,
                  },
                }

                // Auto-toggle for 2-player matches
                if (
                  players.length === 2 &&
                  (status === 'Win' || status === 'Lose')
                ) {
                  const otherPlayer = players.find((p) => p.id !== item.id)
                  if (otherPlayer) {
                    const otherStatus = status === 'Win' ? 'Lose' : 'Win'
                    updates[otherPlayer.id] = {
                      ...editableInputs[otherPlayer.id],
                      matchJoinId: otherPlayer.matchJoinId,
                      userId: otherPlayer.id,
                      claimedStatus: otherStatus,
                      points: otherStatus === 'Win' ? item.potentialWinning : 0,
                    }
                  }
                }

                setEditableInputs((prev) => ({
                  ...prev,
                  ...updates,
                }))
              }}
            />
          )
        },
      },
    ]

    const status = [
      {
        title: 'View',
        key: 'view',
        render: (item) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              title="Details"
              onClick={() => {
                showModal(<ResultModal data={item} />)
              }}
            />
          </div>
        ),
      },
    ]

    return isResult ? [...base, ...editable, ...status] : [...base, ...status]
  }

  const handleSave = async () => {
    const currentMatch = matches.find((m) => m.id === matchId)
    const currentStatus = currentMatch?.status

    if (currentStatus === 'Completed' || currentStatus === 'Cancelled') {
      toast.warn(`Cannot save. Match is already Cancelled or Completed.`)
      return
    }

    const players = expandedData[matchId] || []
    const updated = players.map((user) => {
      const id = user?.user?.id || user?.userId
      const editableData = editableInputs[id] || {}

      return {
        id: user?.id,
        userId: id,
        claimStatus: editableData.claimedStatus || user?.claimedStatus || 0,
        points: Number(editableData.points) || Number(user?.points) || 0,
      }
    })

    if (updated.length === 0) {
      toast.warning('No players found for this match.')
      return
    }

    const payload = { players: updated }

    // Show pending, success, and error toasts
    await toast.promise(
      Match.updateGameResultPlayer({
        id: matchId,
        data: JSON.stringify(payload),
      }).then(async () => {
        await fetchJoinedByUsers(matchId)
      }),
      {
        pending: 'Saving match result...',
        success: 'Match result saved successfully!',
        error: {
          render({ data }: any) {
            return data?.message || 'Failed to save match result'
          },
        },
      },
    )

    window.location.reload()
  }
  return (
    <Fragment>
      <FilterContainer>
        <StatusDropdown
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </StatusDropdown>
      </FilterContainer>
      <div style={{ transform: 'translateY(-60px)' }}>
        <Table
          onSearchChange={(val) => {
            setSearchTerm(val)
          }}
          loading={loading}
          pageControlled={page}
          onPageChange={setPage}
          totalItems={totalItems}
          totalPages={totalPages}
          itemsPerPage={30}
          searchKeys={['number', 'name', 'matchId']}
          text="Player Created Matchrooms"
          icon={outlineuser}
          columns={columns}
          data={data}
          setId={(matchId, limit) => {
            setLimit(limit)
          }}
          setLimit={() => {}}
          expandedRowRender={(row) => {
            const matchId = row.id
            setMatchId(matchId)
            if (!expandedData[matchId]) fetchJoinedByUsers(matchId)
            const players = expandedData[matchId] ?? []
            const childData = players.map((user) => {
              const id = user?.user?.id || user?.userId
              return {
                id,
                player_name: user?.user?.name ?? 'Unknown',
                phone_no: user?.user?.phoneNo ?? 'N/A',
                gamer_tag: user?.infos ?? 'N/A',
                // gamer_tag: user?.user?.infos ?? 'N/A',
                status: user?.status ?? 'N/A',
                points: user?.points ?? '',
                claimedStatus: user?.claimedStatus ?? 'Select',
                matchJoinId: user?.id,
                infos: user?.infos,
                potentialWinning: row.potentialWinning,
                ...(isResult && editableInputs[id]),
              }
            })

            const winCount = childData.filter(
              (p) =>
                (editableInputs[p.id]?.claimedStatus || p.claimedStatus) ===
                'Win',
            ).length
            const loseCount = childData.filter(
              (p) =>
                (editableInputs[p.id]?.claimedStatus || p.claimedStatus) ===
                'Lose',
            ).length
            const refundCount = childData.filter(
              (p) =>
                (editableInputs[p.id]?.claimedStatus || p.claimedStatus) ===
                'Refund',
            ).length

            const canSave =
              (winCount === 1 && loseCount === 1) ||
              refundCount === childData.length

            return (
              <>
                <Table
                  loading={expandedLoading}
                  columns={buildChildColumns(row.entryFee, childData)}
                  data={childData}
                  hasSearchbar={false}
                  hasExportButton={false}
                  borderRadius={0}
                />
                {isResult && (
                  <SaveWrapper>
                    <button disabled={!canSave} onClick={handleSave}>
                      Save Result
                    </button>
                  </SaveWrapper>
                )}
              </>
            )
          }}
          actions={[
            {
              icon: eye,
              onClick: (item) => {
                const fullMatch = matches.find((m) => m?.id === item?.id)
                if (fullMatch) showModal(<MatchroomDetails item={fullMatch} />)
              },
              key: 'view',
            },
          ]}
        />
      </div>
    </Fragment>
  )
}

export default PlayerCreatedMatches

const Input = styled.input`
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  width: 15%;
  text-align: center;
  background-color: white;
  color: ${theme.colors.text};

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    cursor: not-allowed;
    border-color: rgba(255, 255, 255, 0.2);
  }
`

const SaveWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-right: 10px;

  button {
    background-color: ${theme.colors.primary};
    color: white;
    padding: 6px 14px;
    font-size: 13px;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:disabled {
      background-color: #cbd5e1;
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
`

const StatusDropdown = styled.select`
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  background-color: white;
  color: ${theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  transform: translateY(-65px);
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding-right: 30px;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primaryLight};
  }

  &:hover {
    border-color: ${theme.colors.primary};
  }
`

const FilterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  padding: 0 16px;
`
